import type { Role, RouteStaticData } from '@esnafai/shared/auth';
import { createRouter, type RouterHistory } from '@tanstack/react-router';
import { routeTree } from '../routeTree.gen';
import { api, bindRouter, queryClient } from './api';
import type { AuthDeps } from '@esnafai/shared/auth';

// Every search param stays the string the URL carries, so a filter round-trips unchanged (merchant screens §2:
// filters are search params named like the query parameters).
function parseSearch(search: string): Record<string, string> {
  return Object.fromEntries(new URLSearchParams(search));
}

function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function createAppRouter(options: { history?: RouterHistory; context?: AuthDeps } = {}) {
  return createRouter({
    routeTree,
    history: options.history,
    context: options.context ?? { api, queryClient },
    parseSearch,
    stringifySearch,
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

// The app's router; the API client's 401 handler navigates with it.
export const router = createAppRouter();
bindRouter(router);

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
  // What every route carries (UI architecture §3): RouteStaticData of @esnafai/shared/auth.
  interface StaticDataRouteOption {
    nav?: RouteStaticData['nav'];
    roles?: readonly Role[];
    public?: boolean;
  }
  // Waiting's Düzenle opens the conversation view with this draft in the composer (no URL names it).
  interface HistoryState {
    editDraftId?: string;
  }
}
